import { cloneElement, createContext, isValidElement, useContext, type ReactNode } from 'react';
import { normalizeTraditionalAstrologyTerms } from './lib/traditionalTerminology';

export type ChineseLocale = 'zh-Hans' | 'zh-Hant';
export const ChineseLocaleContext = createContext<ChineseLocale>('zh-Hans');
let convert = (text: string) => text;
export function registerTraditionalConverter(converter: (text: string) => string) { convert = converter; }
export const chineseText = (text: string, locale: ChineseLocale) => locale === 'zh-Hant'
  ? normalizeTraditionalAstrologyTerms(convert(text)) : text;

// Translate presentation during React rendering, never mutate its DOM or
// alter form values, event handlers, chart data, IDs, keys or payment records.
export function chineseMarkup(node: ReactNode, locale: ChineseLocale): ReactNode {
  if (locale === 'zh-Hans') return node;
  if (typeof node === 'string') return chineseText(node, locale);
  if (Array.isArray(node)) return node.map((child, index) => {
    const localized = chineseMarkup(child, locale);
    return isValidElement(localized) && localized.key === null ? cloneElement(localized, { key: index }) : localized;
  });
  if (!isValidElement<Record<string, unknown>>(node)) return node;
  const props: Record<string, unknown> = {};
  if (typeof node.type === 'string') {
    for (const key of ['placeholder', 'aria-label', 'title', 'alt']) {
      if (typeof node.props[key] === 'string') props[key] = chineseText(node.props[key] as string, locale);
    }
    if (node.props.lang === 'zh-Hans') props.lang = 'zh-Hant';
    if (typeof node.props.href === 'string' && node.props.href.startsWith('/zh/')) {
      props.href = node.props.href.replace(/^\/zh\//, '/zh-hant/');
    }
  }
  if ('children' in node.props) props.children = chineseMarkup(node.props.children as ReactNode, locale);
  return cloneElement(node, props);
}

export function useChineseMarkup() {
  const locale = useContext(ChineseLocaleContext);
  return (node: ReactNode) => chineseMarkup(node, locale);
}
